import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { emit } from '@/utils/eventBus';
import { useElevenLabsTTS } from '@/hooks/useElevenLabsTTS';
import {
  BookOpen, ArrowLeft, Check, X, Volume2, Eye, Pencil,
  Trophy, Star, Loader2, RefreshCw, ChevronRight
} from 'lucide-react';

const SightWordsPage = () => {
  const { childId } = useParams();
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [mode, setMode] = useState('recognize'); // recognize, spell
  const [userInput, setUserInput] = useState('');
  const [showWord, setShowWord] = useState(false);
  const [result, setResult] = useState(null);
  const [sessionStats, setSessionStats] = useState({ correct: 0, total: 0 });
  const [gradePrompt, setGradePrompt] = useState({ open: false, saving: false, value: 'k', childName: '' });
  const { speak: speakWord } = useElevenLabsTTS({ voice: 'nova', speed: 0.85 });

  useEffect(() => {
    fetchSightWords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  const fetchSightWords = async (overrideGrade) => {
    try {
      setLoading(true);
      // If we've been asked to check grade first, load the child record and
      // pop the grade prompt when it's missing.
      if (!overrideGrade) {
        try {
          const childRes = await api.get(`/children/${childId}`);
          const child = childRes.data || {};
          if (!child.grade_level) {
            setGradePrompt({ open: true, saving: false, value: 'k', childName: child.name || 'your child' });
            setLoading(false);
            return;
          }
        } catch (_e) { /* fall through — fetching sight words will show its own error */ }
      }
      const url = overrideGrade
        ? `/reading/sight-words/${childId}?grade=${encodeURIComponent(overrideGrade)}`
        : `/reading/sight-words/${childId}`;
      const response = await api.get(url);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch sight words:', error);
      toast.error('Failed to load sight words');
    } finally {
      setLoading(false);
    }
  };

  const submitGradePrompt = async () => {
    setGradePrompt((p) => ({ ...p, saving: true }));
    try {
      await api.put(`/children/${childId}`, { grade_level: gradePrompt.value });
      toast.success('Grade saved — loading the right words now');
      // Notify any open dashboard tiles / progress widgets to refresh their child data.
      emit('child:updated', { childId, changes: { grade_level: gradePrompt.value } });
      setGradePrompt({ open: false, saving: false, value: gradePrompt.value, childName: gradePrompt.childName });
      fetchSightWords(gradePrompt.value);
    } catch (err) {
      toast.error('Could not save grade — using kindergarten words for now');
      setGradePrompt((p) => ({ ...p, open: false, saving: false }));
      fetchSightWords('k');
    }
  };

  const handleRecognize = async (recognized) => {
    const currentWord = data.words[currentWordIndex];
    
    try {
      await api.post(`/reading/sight-words/${childId}/practice?word=${currentWord.word}&recognized=${recognized}`);
      
      setResult(recognized ? 'correct' : 'incorrect');
      setSessionStats(prev => ({
        correct: prev.correct + (recognized ? 1 : 0),
        total: prev.total + 1
      }));
      
      setTimeout(() => {
        setResult(null);
        setShowWord(false);
        if (currentWordIndex < data.words.length - 1) {
          setCurrentWordIndex(prev => prev + 1);
        } else {
          toast.success('Practice complete! Great job!');
        }
      }, 1500);
    } catch (error) {
      toast.error('Failed to save progress');
    }
  };

  const handleSpellSubmit = async () => {
    const currentWord = data.words[currentWordIndex];
    const isCorrect = userInput.toLowerCase().trim() === currentWord.word.toLowerCase();
    
    try {
      await api.post(`/reading/sight-words/${childId}/practice?word=${currentWord.word}&spelled_correctly=${isCorrect}`);
      
      setResult(isCorrect ? 'correct' : 'incorrect');
      setSessionStats(prev => ({
        correct: prev.correct + (isCorrect ? 1 : 0),
        total: prev.total + 1
      }));
      
      if (!isCorrect) {
        setShowWord(true);
      }
      
      setTimeout(() => {
        setResult(null);
        setUserInput('');
        setShowWord(false);
        if (currentWordIndex < data.words.length - 1) {
          setCurrentWordIndex(prev => prev + 1);
        } else {
          toast.success('Practice complete! Great job!');
        }
      }, 2000);
    } catch (error) {
      toast.error('Failed to save progress');
    }
  };

  const resetPractice = () => {
    setCurrentWordIndex(0);
    setSessionStats({ correct: 0, total: 0 });
    setResult(null);
    setUserInput('');
    setShowWord(false);
  };

  if (gradePrompt.open) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white px-4">
        <Card className="max-w-md w-full" data-testid="sight-words-grade-prompt">
          <CardHeader>
            <CardTitle>What grade is {gradePrompt.childName} in?</CardTitle>
            <CardDescription>
              We'll pick the right sight words for their level. You can change this later in the child's profile.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: 'prek', label: 'Pre-K' },
                { key: 'k',    label: 'Kindergarten' },
                { key: '1',    label: 'Grade 1' },
                { key: '2',    label: 'Grade 2' },
                { key: '3',    label: 'Grade 3' },
                { key: '4',    label: 'Grade 4+' },
              ].map((g) => (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => setGradePrompt((p) => ({ ...p, value: g.key }))}
                  className={`rounded-lg border-2 px-3 py-2 text-sm font-medium transition ${
                    gradePrompt.value === g.key
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-blue-300'
                  }`}
                  data-testid={`sight-words-grade-${g.key}`}
                >
                  {g.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="ghost"
                onClick={() => { setGradePrompt((p) => ({ ...p, open: false })); fetchSightWords('k'); }}
                data-testid="sight-words-grade-skip"
              >
                Skip — use kindergarten
              </Button>
              <Button
                onClick={submitGradePrompt}
                disabled={gradePrompt.saving}
                data-testid="sight-words-grade-confirm"
              >
                {gradePrompt.saving ? 'Saving…' : 'Save grade & continue'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const currentWord = data?.words?.[currentWordIndex];
  const progress = data?.words ? ((currentWordIndex) / data.words.length) * 100 : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="font-bold text-xl text-slate-800">Daily Sight Words</h1>
                  <p className="text-sm text-slate-500">
                    Grade {data?.grade} | {data?.mastered_count}/{data?.total_words} mastered
                  </p>
                </div>
              </div>
            </div>
            <Button variant="outline" onClick={resetPractice} size="sm">
              <RefreshCw className="w-4 h-4 mr-1" /> Reset
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-sm mb-2">
            <span>Progress</span>
            <span>{currentWordIndex + 1} of {data?.words?.length}</span>
          </div>
          <Progress value={progress} className="h-3" />
        </div>

        {/* Mode Selector */}
        <div className="flex gap-4 mb-8 justify-center">
          <Button
            variant={mode === 'recognize' ? 'default' : 'outline'}
            onClick={() => setMode('recognize')}
            className={mode === 'recognize' ? 'bg-blue-600' : ''}
            data-testid="mode-recognize"
          >
            <Eye className="w-4 h-4 mr-2" /> Read & Recognize
          </Button>
          <Button
            variant={mode === 'spell' ? 'default' : 'outline'}
            onClick={() => setMode('spell')}
            className={mode === 'spell' ? 'bg-green-600' : ''}
            data-testid="mode-spell"
          >
            <Pencil className="w-4 h-4 mr-2" /> Spell Out
          </Button>
        </div>

        {/* Practice Area */}
        {currentWord && currentWordIndex < data.words.length ? (
          <Card className={`max-w-lg mx-auto transition-all ${
            result === 'correct' ? 'border-green-500 bg-green-50' :
            result === 'incorrect' ? 'border-red-500 bg-red-50' : ''
          }`}>
            <CardContent className="p-8 text-center">
              {mode === 'recognize' ? (
                <>
                  {/* Recognition Mode */}
                  <div className="mb-8">
                    <p className="text-6xl font-bold text-slate-800 mb-4" data-testid="current-word">
                      {currentWord.word}
                    </p>
                    <Button
                      variant="ghost"
                      onClick={() => speakWord(currentWord.word)}
                      className="text-blue-600"
                    >
                      <Volume2 className="w-5 h-5 mr-2" /> Hear Word
                    </Button>
                  </div>

                  {result ? (
                    <div className="flex items-center justify-center gap-2 text-2xl">
                      {result === 'correct' ? (
                        <>
                          <Check className="w-8 h-8 text-green-500" />
                          <span className="text-green-600 font-bold">Great job!</span>
                        </>
                      ) : (
                        <>
                          <X className="w-8 h-8 text-red-500" />
                          <span className="text-red-600 font-bold">Keep practicing!</span>
                        </>
                      )}
                    </div>
                  ) : (
                    <div>
                      <p className="text-lg text-slate-600 mb-4">Can you read this word?</p>
                      <div className="flex gap-4 justify-center">
                        <Button
                          size="lg"
                          onClick={() => handleRecognize(true)}
                          className="bg-green-600 hover:bg-green-700 px-8"
                          data-testid="btn-yes"
                        >
                          <Check className="w-5 h-5 mr-2" /> Yes!
                        </Button>
                        <Button
                          size="lg"
                          variant="outline"
                          onClick={() => handleRecognize(false)}
                          className="px-8"
                          data-testid="btn-no"
                        >
                          <X className="w-5 h-5 mr-2" /> Not yet
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Spelling Mode */}
                  <div className="mb-8">
                    <Button
                      size="lg"
                      onClick={() => speakWord(currentWord.word)}
                      className="bg-blue-600 hover:bg-blue-700 mb-4"
                    >
                      <Volume2 className="w-5 h-5 mr-2" /> Hear the Word
                    </Button>
                    <p className="text-slate-600">Listen and spell the word</p>
                  </div>

                  {showWord && (
                    <div className="mb-4 p-4 bg-amber-100 rounded-lg">
                      <p className="text-sm text-amber-700">The correct spelling is:</p>
                      <p className="text-3xl font-bold text-amber-800">{currentWord.word}</p>
                    </div>
                  )}

                  {result ? (
                    <div className="flex items-center justify-center gap-2 text-2xl">
                      {result === 'correct' ? (
                        <>
                          <Check className="w-8 h-8 text-green-500" />
                          <span className="text-green-600 font-bold">Perfect spelling!</span>
                        </>
                      ) : (
                        <>
                          <X className="w-8 h-8 text-red-500" />
                          <span className="text-red-600 font-bold">Try again next time!</span>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Input
                        type="text"
                        placeholder="Type the word..."
                        value={userInput}
                        onChange={(e) => setUserInput(e.target.value)}
                        className="text-center text-2xl h-14"
                        autoFocus
                        onKeyDown={(e) => e.key === 'Enter' && handleSpellSubmit()}
                        data-testid="spell-input"
                      />
                      <Button
                        size="lg"
                        onClick={handleSpellSubmit}
                        className="bg-green-600 hover:bg-green-700 w-full"
                        disabled={!userInput.trim()}
                        data-testid="btn-check"
                      >
                        Check Spelling
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="max-w-lg mx-auto text-center">
            <CardContent className="p-8">
              <Trophy className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Practice Complete!</h2>
              <p className="text-slate-600 mb-6">
                You got {sessionStats.correct} out of {sessionStats.total} correct!
              </p>
              <div className="flex gap-4 justify-center">
                <Button onClick={resetPractice} variant="outline">
                  Practice Again
                </Button>
                <Link to="/dashboard">
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    Back to Dashboard
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Session Stats */}
        {sessionStats.total > 0 && (
          <div className="mt-8 flex justify-center gap-8">
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">{sessionStats.correct}</p>
              <p className="text-sm text-slate-500">Correct</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-slate-600">{sessionStats.total}</p>
              <p className="text-sm text-slate-500">Total</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">
                {Math.round((sessionStats.correct / sessionStats.total) * 100)}%
              </p>
              <p className="text-sm text-slate-500">Accuracy</p>
            </div>
          </div>
        )}

        {/* Word List */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-lg">All Sight Words</CardTitle>
            <CardDescription>Your progress on each word</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {data?.words?.map((word, idx) => (
                <span
                  key={word.word}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                    word.mastered 
                      ? 'bg-green-100 text-green-700' 
                      : idx === currentWordIndex
                        ? 'bg-blue-500 text-white'
                        : idx < currentWordIndex
                          ? 'bg-slate-200 text-slate-600'
                          : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {word.word}
                  {word.mastered && <Star className="w-3 h-3 inline ml-1" />}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default SightWordsPage;
